import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Bot, X, Send, Phone, MessageCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { Logo } from "./Reveal";

const WA = "https://wa.me/919008177176?text=" + encodeURIComponent("Hello Roxy Cables, I need help choosing a cable.");

const CHIPS = [
    "Find me a 95 sqmm 11 kV HT cable",
    "Do you stock Dowells glands & lugs?",
    "2.5 sqmm 3 core flexible FRLS price?",
    "Talk to a human",
];

function getSid() {
    let sid = localStorage.getItem("roxy_sid");
    if (!sid) {
        sid = "web_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("roxy_sid", sid);
    }
    return sid;
}

export default function RoxyAI() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [callback, setCallback] = useState(false);
    const [cbForm, setCbForm] = useState({ name: "", phone: "" });
    const scrollRef = useRef(null);
    const sid = useRef(getSid());

    useEffect(() => {
        const h = () => setOpen(true);
        window.addEventListener("roxy:ai-open", h);
        return () => window.removeEventListener("roxy:ai-open", h);
    }, []);

    useEffect(() => {
        if (open && messages.length === 0) {
            api.get(`/chat/history?session_id=${sid.current}`)
                .then((res) => setMessages(res.data))
                .catch(() => {});
        }
    }, [open]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, open]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        if (msg.toLowerCase().includes("human")) {
            window.open(WA, "_blank", "noopener,noreferrer");
            return;
        }
        setInput("");
        setSending(true);
        setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
        try {
            const res = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sid.current, message: msg }),
            });
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let acc = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                acc += dec.decode(value, { stream: true });
                setMessages((m) => {
                    const c = [...m];
                    c[c.length - 1] = { role: "assistant", text: acc };
                    return c;
                });
            }
        } catch {
            setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", text: "Connection hiccup — please try again or WhatsApp our desk on +91 900 81 77 176." };
                return c;
            });
        } finally {
            setSending(false);
        }
    };

    const sendCallback = async (e) => {
        e.preventDefault();
        try {
            await api.post("/enquiries", {
                name: cbForm.name,
                phone: cbForm.phone,
                email: "",
                interest: "Roxy AI — Callback",
                size: "",
                message: "Callback requested via Roxy AI chat",
            });
            toast.success("Callback requested — our desk will call you.");
            setCallback(false);
            setCbForm({ name: "", phone: "" });
        } catch {
            toast.error("Could not send — please WhatsApp us instead.");
        }
    };

    return (
        <>
            <button
                data-testid="roxy-ai-button"
                onClick={() => setOpen(!open)}
                aria-label="Open Roxy AI assistant"
                className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-full bg-ink text-white flex items-center justify-center shadow-[0_12px_32px_rgba(15,36,64,0.4)] hover:scale-105 transition-transform duration-300"
            >
                {open ? <X size={22} /> : <Bot size={24} />}
                {!open && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-copper-bright animate-ping" />}
                {!open && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-copper-bright" />}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="roxy-ai-panel"
                        className="fixed bottom-40 right-4 sm:right-5 z-50 w-[calc(100vw-2rem)] max-w-[390px] h-[540px] max-h-[70vh] bg-white rounded-md border border-line shadow-2xl flex flex-col overflow-hidden"
                        initial={{ opacity: 0, y: 30, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30, scale: 0.97 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="bg-ink text-white px-5 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Logo size={26} />
                                <div>
                                    <p className="font-display font-semibold text-base leading-none">Roxy AI</p>
                                    <p className="font-mono text-[9px] tracking-[0.2em] text-white/50 uppercase mt-1 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Catalogue-trained · Online
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="roxy-ai-callback-header"
                                    onClick={() => setCallback((c) => !c)}
                                    aria-label="Request a callback"
                                    title="Request a callback"
                                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-copper flex items-center justify-center transition-colors duration-300"
                                >
                                    <Phone size={13} />
                                </button>
                                <a
                                    data-testid="roxy-ai-handoff"
                                    href={WA}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-[9px] uppercase tracking-[0.15em] bg-white/10 hover:bg-[#25D366] px-3 py-2 rounded-full transition-colors duration-300 flex items-center gap-1.5"
                                >
                                    <MessageCircle size={11} /> Human
                                </a>
                            </div>
                        </div>

                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-paper" data-testid="roxy-ai-messages">
                            {messages.length === 0 && (
                                <div className="space-y-3">
                                    <div className="bg-white border border-line rounded-md rounded-tl-none p-3 text-sm text-ink max-w-[85%]">
                                        Namaste! I'm Roxy AI — trained on our live catalogue. Ask me to find a
                                        cable, explain a spec, or prepare an enquiry.
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {CHIPS.map((c) => (
                                            <button
                                                key={c}
                                                data-testid={`roxy-ai-chip-${c.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                onClick={() => send(c)}
                                                className="font-mono text-[10px] border border-copper/40 text-copper rounded-full px-3 py-1.5 hover:bg-copper hover:text-white transition-colors duration-300"
                                            >
                                                {c}
                                            </button>
                                        ))}
                                        <button
                                            data-testid="roxy-ai-callback-toggle"
                                            onClick={() => setCallback(true)}
                                            className="font-mono text-[10px] border border-line text-mist rounded-full px-3 py-1.5 hover:border-copper hover:text-copper transition-colors duration-300 flex items-center gap-1"
                                        >
                                            <Phone size={10} /> Get a callback
                                        </button>
                                    </div>
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`max-w-[85%] text-sm leading-relaxed p-3 rounded-md whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-ink text-white ml-auto rounded-br-none"
                                            : "bg-white border border-line text-ink rounded-tl-none"
                                    }`}
                                >
                                    {m.text || (sending && i === messages.length - 1 ? "…" : "")}
                                </div>
                            ))}
                            {callback && (
                                <form onSubmit={sendCallback} className="bg-copper-light border border-copper/30 rounded-md p-3 space-y-2" data-testid="roxy-ai-callback-form">
                                    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-copper">Callback request — we only use this to call you back</p>
                                    <input
                                        data-testid="roxy-ai-callback-name"
                                        required
                                        value={cbForm.name}
                                        onChange={(e) => setCbForm({ ...cbForm, name: e.target.value })}
                                        placeholder="Your name"
                                        className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-copper"
                                    />
                                    <input
                                        data-testid="roxy-ai-callback-phone"
                                        required
                                        value={cbForm.phone}
                                        onChange={(e) => setCbForm({ ...cbForm, phone: e.target.value })}
                                        placeholder="Phone"
                                        type="tel"
                                        className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-copper"
                                    />
                                    <button data-testid="roxy-ai-callback-submit" className="w-full rounded-full bg-copper text-white font-mono text-[10px] uppercase tracking-[0.15em] py-2.5 hover:bg-copper-bright transition-colors duration-300">
                                        Request Callback
                                    </button>
                                </form>
                            )}
                        </div>

                        <form
                            onSubmit={(e) => { e.preventDefault(); send(); }}
                            className="border-t border-line p-3 flex gap-2 bg-white"
                        >
                            <input
                                data-testid="roxy-ai-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about cables, specs, brands..."
                                className="flex-1 bg-paper border border-line rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-dim focus:outline-none focus:border-copper transition-colors duration-300"
                            />
                            <button
                                data-testid="roxy-ai-send"
                                type="submit"
                                disabled={sending}
                                className="w-10 h-10 rounded-full bg-copper text-white flex items-center justify-center hover:bg-copper-bright transition-colors duration-300 disabled:opacity-50"
                                aria-label="Send message"
                            >
                                <Send size={15} />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
